// JOBE-0105: ProjectsTable — the Index page's main column. Renders the
// column header, the chip composer (via the library's ChipRow), the
// body (states machine: loading / cedar-denied / error / empty / rows
// + Load More), and the page-level rollups-truncated banner.
//
// Composition: hook-only (no <EntityBrowser> host wrap) — the page
// passes hook state in via props per spec §Decision 5.

import React from 'react';
import { ChipRow } from '../library/ChipRow.jsx';
import { ProjectRow } from './ProjectRow.jsx';

export function ProjectsTable({
  rows,
  nextCursor,
  loadState,
  error,
  rollupsTruncated,
  chips,
  onChipsChange,
  onLoadMore,
  onRetry,
  selectedUrn,
  onSelect,
  roster,
}) {
  return (
    <div className="projects-table" data-testid="projects-table">
      <ChipRow
        chips={chips}
        onChange={onChipsChange}
        roster={roster}
      />
      {rollupsTruncated ? (
        <div
          className="projects-rollups-truncated muted"
          data-testid="projects-rollups-truncated"
          role="note"
        >
          Per-row counts are shown for the first page only — load more to
          see additional rows (counts may be capped on the server).
        </div>
      ) : null}
      <div className="projects-row projects-row-header" role="row">
        <div className="projects-row-cell" aria-hidden="true" />
        <div className="projects-row-cell projects-row-title-header">Project</div>
        <div className="projects-row-cell">Status</div>
        <div className="projects-row-cell">Owner</div>
        <div className="projects-row-cell">Tasks</div>
        <div className="projects-row-cell">Documents</div>
        <div className="projects-row-cell">Entities</div>
      </div>

      <ProjectsTableBody
        rows={rows}
        loadState={loadState}
        error={error}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        onRetry={onRetry}
        selectedUrn={selectedUrn}
        onSelect={onSelect}
        roster={roster}
      />
    </div>
  );
}

function ProjectsTableBody({
  rows, loadState, error, nextCursor, onLoadMore, onRetry,
  selectedUrn, onSelect, roster,
}) {
  if (loadState === 'cedar-denied') {
    return (
      <div className="projects-table-state projects-table-denied" data-testid="projects-denied">
        You don&apos;t have permission to list projects for this scope.
      </div>
    );
  }
  if (loadState === 'error') {
    return (
      <div className="projects-table-state projects-table-error" data-testid="projects-error">
        <p>
          Couldn&apos;t load projects
          {error?.status ? ` (status ${error.status})` : ''}.
        </p>
        <button
          type="button"
          className="projects-table-retry"
          data-testid="projects-retry"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  }
  if (loadState === 'loading' && (!rows || rows.length === 0)) {
    return (
      <div className="projects-table-state projects-table-loading" data-testid="projects-loading">
        Loading projects…
      </div>
    );
  }
  if (loadState === 'ready' && (!rows || rows.length === 0)) {
    return (
      <div className="projects-table-state projects-table-empty" data-testid="projects-empty">
        <p>No projects for this scope.</p>
        <p className="muted">Try a different scope or clear filters.</p>
      </div>
    );
  }
  return (
    <div className="projects-table-body" data-testid="projects-table-body">
      {(rows || []).map((row) => (
        <ProjectRow
          key={row.urn}
          row={row}
          roster={roster}
          selected={selectedUrn === row.urn}
          onSelect={onSelect}
        />
      ))}
      {nextCursor ? (
        <div className="projects-table-load-more">
          <button
            type="button"
            className="projects-load-more"
            data-testid="projects-load-more"
            onClick={onLoadMore}
            disabled={loadState === 'loading'}
          >
            {loadState === 'loading' ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
